<template>
  <div class="palette-view">
    <palette-header
      :number="number"
      :numbers="numbers"
      :colorFocus="colorFocus"
      :colorRange="colorRange"
      :colorFeeling="colorFeeling"
      :onNumber="handleNumber"
      :onColorFocus="handleColorFocusUpdate"
      :onColorRange="handleColorRangeUpdate"
      :onColorFeeling="handleColorFeelingUpdate"
      :clearFilter="clearFilter"
      :showClear="showClear"
    />

    <div class="palette"
         :class="{hover: showInfo}"
         @mousemove="handleInfo(true)"
         @mouseleave="handleInfo(false)"
         @click="showHint = 0"
    >
      <div class="colors" ref="color">
        <div class="inner"
             v-for="(color, id) in colors"
             :key="id"
             :class="{
               backFar: id < currentColor - 1,
               back: id < currentColor,
               current: id === currentColor,
               next: id > currentColor,
               nextFar: id > currentColor + 1,
             }"
             @click="maybeNavigate"
        >
          <color-palette-item v-for="(item, index) in color"
                              :key="index"
                              :model="item"
                              :active="showInfo && id === currentColor"
                              :onChange="handleColorChange(id, index)"
                              :onFocus="handleColorFocus"
                              :onBlur="handleColorBlur"
                              :onUpdate="handleUpdateColor"
                              v-show="index < number"
          />
        </div>
      </div>

      <div class="arrow back" @click="back(); editing = true" v-show="currentColor > 0">
        <i class="material-icons">arrow_back</i>
      </div>

      <div class="arrow next" @click="next(); editing = true" v-show="currentColor < colors.length - 1">
        <i class="material-icons">arrow_forward</i>
      </div>

      <div class="actions" v-for="(color, id) in colors" v-show="id === currentColor" :key="id">
        <a class="hoverable" :class="{active: saved[id]}" @click="(e) => handleSave(e, id)">
          <i class="material-icons" v-show="!saved[id]">star_border</i>
          <i class="material-icons" v-show="saved[id]">star</i>
          <span v-show="saved[id]" class="text hidden-phone">Saved!</span>

          <div class="popup">
            <div class="label">Save Palette</div>
          </div>
        </a>

        <a class="hoverable" @click="(e) => handleExport(e, id)">
          <i class="material-icons">file_download</i>

          <div class="popup">
            <div class="label">Export</div>
          </div>
        </a>
      </div>

      <img src="../assets/hint1.png" alt="" class="hint hint-1" :class="{active: showHint === 1}"/>

      <img src="../assets/hint2.png" alt="" class="hint hint-2" :class="{active: showHint === 2}"/>

      <img src="../assets/hint3.png" alt="" class="hint hint-3" :class="{active: showHint === 1}"/>

      <!-- <div class="slider" :style="{width: (1 / number * 100) + '%'}">
        <color-slider/>
      </div> -->
    </div>

    <div class="container">
      <color-line-vertical :colors="colors[currentColor]" :max="number" class="hidden-tablet"/>

      <palette-sample-list
        :showHint="showEnter"
        :items="palettePreviews"
        :totalNumberOfStyleRules="totalNumberOfStyleRules"
        :colors="colorsUrl"
        :onUIClick="onUIClick"
        :handlePreview="handlePreview"
      />
    </div>
  </div>
</template>

<script>
import Hammer from 'hammerjs';
import axios from 'axios';
import { mapActions } from 'vuex';
import moment from 'moment';
import fileSaver from 'file-saver';
import {
  key, removeFocus, MAXIMUM_COLOR_PALETTES, validateHex,
} from '../utils/helper';
import { getSession, setSession, removeSession } from '../utils/session';
import { sendRequest } from '../utils/api';
import PaletteHeader from './PaletteHeader';
import ColorLineVertical from '../components/color/ColorLineVertical';
import ColorPaletteItem from '../components/color/ColorPaletteItem';
import PaletteSampleList from '../components/palette/PaletteSampleList';
import router from '../router';
import store from '../store';

export default {
  components: {
    PaletteSampleList,
    ColorPaletteItem,
    PaletteHeader,
    ColorLineVertical,
  },
  data() {
    return {
      colors: [],
      colorPalettes: [],
      currentColor: 0,
      number: 4,
      colorFocus: {
        placeholder: true,
      },
      colorRange: {
        placeholder: true,
      },
      colorFeeling: {
        placeholder: true,
      },
      numbers: [5, 4, 3, 2],
      showInfo: false,
      showInfoTimeout: null,
      showHint: getSession('paletteHintShown') ? 0 : 1,
      showEnter: false,
      editing: false,
      spaceTime: 0,
      saved: {},
      mc: {},
      palettePreviews: [],
      totalNumberOfStyleRules: 0,
      colorsUrl: '',
      isFirstColorEnabled: false,
      delayPreviews: undefined,
      generatePreviewToken: axios.CancelToken.source(),
      isStart: true,
      showClear: true,
      outputs: [],
    };
  },
  created() {
    document.addEventListener('keydown', this.keyEvent);
    if (this.$route.params.id) {
      this.colorsUrl = this.$route.params.id;
      this.number = this.colorsUrl.split('-').length;
      setSession('palette-number', this.number);
      const storedColorFocus = getSession('palette-colorFocus');
      if (storedColorFocus) {
        this.colorFocus = storedColorFocus;
      }

      const storedColorRange = getSession('palette-colorRange');
      if (storedColorRange) {
        this.colorRange = storedColorRange;
      }

      const storedColorFeeling = getSession('palette-colorFeeling');
      if (storedColorFeeling) {
        this.colorFeeling = storedColorFeeling;
      }

      if (!storedColorFocus && !storedColorRange && !storedColorFeeling) {
        this.showClear = false;
      }
      this.initializePalettes();
    } else {
      this.colorsUrl = '';

      const storedNumber = getSession('palette-number');
      if (storedNumber !== undefined) {
        this.number = parseInt(storedNumber, 10);
      } else {
        this.number = 4;
      }

      const storedColorFocus = getSession('palette-colorFocus');
      if (storedColorFocus) {
        this.colorFocus = storedColorFocus;
      }

      const storedColorRange = getSession('palette-colorRange');
      if (storedColorRange) {
        this.colorRange = storedColorRange;
      }

      const storedColorFeeling = getSession('palette-colorFeeling');
      if (storedColorFeeling) {
        this.colorFeeling = storedColorFeeling;
      }
      if (getSession('loggedin')) {
        removeSession('loggedin');
        this.addNotification({ message: 'You have logged in!' });
      }

      if (!storedColorFocus && !storedColorRange && !storedColorFeeling) {
        this.showClear = false;
      }
      this.updatePalettes();
    }
  },
  mounted() {
    removeFocus();
    this.mc = new Hammer.Manager(this.$refs.color);
    this.mc.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL }));
    this.mc.on('swiperight', this.back);
    this.mc.on('swipeleft', this.next);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyEvent);
    this.mc.off('swiperight', this.back);
    this.mc.off('swipeleft', this.next);
    this.mc.destroy();

    if (this.showInfoTimeout) {
      clearTimeout(this.showInfoTimeout);
    }
  },
  watch: {
    $route() {
      removeFocus();
      if (this.$route.params.id) {
        this.colorsUrl = this.$route.params.id;
      } else {
        this.colorsUrl = '';
      }

      const isRoot = getSession('isRoot');
      if (isRoot) {
        removeSession('isRoot');
        store.dispatch('toggleHome', true);
      }
    },
    number(to, from) {
      if (to === from) {
        return;
      }
      if (this.isStart) {
        return;
      }
      this.updatePalettes();
    },
    colorFocus(to, from) {
      if (to.color === from.color) {
        return;
      }
      if (this.isStart) {
        return;
      }
      this.updatePalettes();
    },
    colorRange(to, from) {
      if (to.value === from.value) {
        return;
      }
      if (this.isStart) {
        return;
      }
      this.updatePalettes();
    },
    colorFeeling(to, from) {
      if (to.value === from.value) {
        return;
      }
      if (this.isStart) {
        return;
      }
      this.updatePalettes();
    },
    currentColor(to) {
      if (
        (to % this.colors.length > this.colors.length - MAXIMUM_COLOR_PALETTES)
          && (to % MAXIMUM_COLOR_PALETTES === MAXIMUM_COLOR_PALETTES - 4)
      ) {
        this.appendColorPalettes();
      }
      this.changeRoute();
    },
  },
  methods: {
    ...mapActions(['toggleAuth', 'setUser']),
    ...mapActions('notification', [
      'addNotification',
    ]),
    clearFilter() {
      this.isStart = true;
      this.colorFocus = {
        placeholder: true,
      };
      this.colorFeeling = {
        placeholder: true,
      };
      this.isStart = false;
      this.colorRange = {
        placeholder: true,
      };
      this.showClear = false;
      removeSession('palette-colorFocus');
      removeSession('palette-colorRange');
      removeSession('palette-colorFeeling');
    },
    handleNumber(id) {
      this.isStart = false;
      this.number = id;
      setSession('palette-number', id);
    },
    handleColorFocusUpdate(value) {
      this.isStart = false;
      this.colorFocus = value;
      this.showClear = true;
      setSession('palette-colorFocus', value);
    },
    handleColorRangeUpdate(value) {
      this.isStart = false;
      this.colorRange = value;
      this.showClear = true;
      setSession('palette-colorRange', value);
    },
    handleColorFeelingUpdate(value) {
      this.isStart = false;
      this.colorFeeling = value;
      this.showClear = true;
      setSession('palette-colorFeeling', value);
    },
    updatePalettes() {
      const params = [`count=${MAXIMUM_COLOR_PALETTES}`, `colors=${this.number}`];

      if (!this.colorFocus.placeholder) {
        params.push(`hue-min=${encodeURIComponent(this.colorFocus.hue_min)}`, `hue-max=${encodeURIComponent(this.colorFocus.hue_max)}`, `reversed=${encodeURIComponent(this.colorFocus.reversed)}`);
      }

      if (!this.colorRange.placeholder) {
        params.push(`hues=${encodeURIComponent(this.colorRange.value)}`);
      }

      if (!this.colorFeeling.placeholder) {
        params.push(`feeling=${encodeURIComponent(this.colorFeeling.value)}`);
      }

      if (window.location.href.indexOf('/fluid') >= 0 && this.$route.query.distance) {
        params.push(`distance=${encodeURIComponent(this.$route.query.distance)}`);
      }

      sendRequest(`/color-palettes/generate?${params.join('&')}`, 'get', { Authorization: getSession('jwtoken') }, {})
        .then((response) => {
          this.colors = response.data.colorPalettes.map(palette => palette.colors);
          this.colorPalettes = response.data.colorPalettes;
          this.currentColor = 0;
          this.changeRoute();
          this.getPreviews();
          this.saved = {};
        })
        .catch((err) => {
          console.log(err);
        });
    },
    initializePalettes() {
      const url = `/color-palettes/generate?count=${MAXIMUM_COLOR_PALETTES}&colors=${this.number}&firstColor=${this.colorsUrl}`;

      sendRequest(url, 'get', { Authorization: getSession('jwtoken') }, {})
        .then((response) => {
          this.colors = response.data.colorPalettes.map(palette => palette.colors);
          this.colorPalettes = response.data.colorPalettes;
          this.currentColor = 0;
          this.changeRoute();
          this.getPreviews();
          this.saved = {};
        })
        .catch((err) => {
          console.log(err);
        });
    },
    appendColorPalettes() {
      let url = `/color-palettes/generate?count=${MAXIMUM_COLOR_PALETTES}&colors=${this.number}`;
      if (!this.colorFocus.placeholder) {
        url = `${url}&hue-min=${this.colorFocus.hue_min}&hue-max=${this.colorFocus.hue_max}&reversed=${this.colorFocus.reversed}`;
      }

      if (!this.colorRange.placeholder) {
        url = `${url}&hues=${this.colorRange.value}`;
      }

      if (!this.colorFeeling.placeholder) {
        url = `${url}&feeling=${this.colorFeeling.value}`;
      }
      sendRequest(url, 'get', { Authorization: getSession('jwtoken') }, {})
        .then((response) => {
          this.colors = this.colors
            .concat(response.data.colorPalettes.map(palette => palette.colors));
          this.colorPalettes.concat(response.data.colorPalettes);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    changeRoute() {
      const currentColor = this.colors[this.currentColor];
      let query = currentColor.map(color => color.substr(1)).join('-');
      const validColors = validateHex(query);

      let i = 0;
      for (i = 0; i < currentColor.length; i++) {
        if (`#${validColors[i]}` !== currentColor[i]) {
          break;
        }
      }

      if (i === currentColor.length) {
        query = validColors.join('-');

        /**
           * Temporary experimental parameter for Jamison
           */
        if (window.location.search && window.location.search.length > 0) {
          query += window.location.search;
        }

        if (window.location.href.indexOf('/fluid') >= 0) {
          router.push(`/palette/fluid/${query}`);
        } else {
          router.push(`/palette/${query}`);
        }
      } else {
        this.colors[this.currentColor] = validColors.map(color => `#${color}`);
        query = validColors.join('-');

        if (window.location.href.indexOf('/fluid') >= 0) {
          router.replace(`/palette/fluid/${query}`);
        } else {
          router.replace(`/palette/${query}`);
        }
      }
    },
    handleColorChange(id, index) {
      return (color) => {
        this.$set(this.colors[id], index, color);
      };
    },
    handleUpdateColor() {
      this.changeRoute();
      this.delayPreviews = setTimeout(() => this.getPreviews(), 300);
    },
    handleSave(e, id) {
      if (e) {
        e.stopPropagation();
      }
      if (this.saved[id]) {
        return;
      }
      sendRequest(
        '/palette-events',
        'post',
        { Authorization: getSession('jwtoken') },
        {
          'event-type': 'save-palette',
          ...this.colorPalettes[id],
        },
      )
        .then(() => {
          this.$set(this.saved, id, !this.saved[id]);
          this.addNotification({ message: this.saved[id] ? 'Palette saved!' : 'Palette removed!' });
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            removeSession('jwtoken');
            this.setUser({ user: null, isAuthenticated: false });
            this.toggleAuth({ show: true, initialAuthTab: 1 });
            this.addNotification({ message: 'To save the palettes, please login.' });
          } else {
            this.addNotification({ message: 'Something went wrong.' });
          }
        });
    },
    go(step) {
      setSession('paletteHintShown', true);
      this.handleHint();

      const now = this.currentColor + step;

      if (now >= 0 && now < this.colors.length) {
        this.currentColor = now;

        if (this.delayPreviews) {
          clearTimeout(this.delayPreviews);
        }
        this.delayPreviews = setTimeout(() => this.getPreviews(), 300);
      }
    },
    getPreviews() {
      let NAME = 'App Landing Page';
      let LAYOUT = 'Website';
      this.generatePreviewToken.cancel('Cancelled');
      this.generatePreviewToken = axios.CancelToken.source();
      return new Promise((res) => {
        let paletteColors = this.colors[this.currentColor].map(c => `color=${c.substr(1).toLowerCase()}`).join('&');
        if (paletteColors !== '' && getSession('thumb_asset_id')) {
          paletteColors = `${paletteColors}&output=${getSession('thumb_asset_id')}`;
        }
        sendRequest(`/color-themes/match?${paletteColors}`, 'get', { Authorization: getSession('jwtoken') }, {}, 'json', this.generatePreviewToken)
          .then((response) => {
            const styleRules = response.data.outputs;
            this.outputs = styleRules;
            const totalStyleRules = response.data['total-matches'];
            if (styleRules.length === 0) {
              this.palettePreviews = [];
            } else {
              this.palettePreviews = [];
              for (let i = 0; i < styleRules.length; i++) {
                if (this.palettePreviews.length === 11) break;

                if (getSession('currentSample') && getSession('currentLayout')) {
                  NAME = getSession('currentSample');
                  LAYOUT = getSession('currentLayout');
                }
                for (let j = 0; j < styleRules[i]['asset-visuals'].svgs.length; j++) {
                  if (styleRules[i]['asset-visuals'].svgs[j].name === NAME && (styleRules[i]['asset-visuals'].svgs[j].platform || [])[0] === LAYOUT) {
                    this.palettePreviews.push(styleRules[i]['asset-visuals'].svgs[j]);
                    break;
                  }
                }
              }
            }
            this.totalNumberOfStyleRules = totalStyleRules;
            res();
          })
          .catch((err) => {
            console.log(err);
          });
      });
    },
    back() {
      this.go(-1);
    },
    next() {
      this.go(1);
    },
    handleExport(e, id) {
      if (e) {
        e.stopPropagation();
      }
      this.addNotification({ message: 'Exporting color palette...' });
      sendRequest(
        '/color-palettes/export',
        'post',
        {
          Authorization: getSession('jwtoken'),
          Accept: 'application/octet-stream',
        },
        this.colorPalettes[id],
        'arraybuffer',
      )
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/octet-stream' });
          fileSaver.saveAs(blob, `colorful-palette-${moment().format('YYYY-MM-DD-hh.mm.ss')}.pdf`);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            this.addNotification({ message: 'You should log in or upgrade your account to export palette.' });
          } else {
            this.addNotification({ message: 'Something went wrong!' });
          }
        });
    },
    handleHint() {
      if (this.showHint > 0) {
        this.showHint++;

        setTimeout(() => {
          this.showHint = 0;
        }, 1000);
      }
    },
    handleInfo(value) {
      this.showInfo = value;

      if (this.showInfoTimeout) {
        clearTimeout(this.showInfoTimeout);
      }

      this.showInfoTimeout = setTimeout(() => {
        if (!this.editing) {
          this.showInfo = false;
        }
      }, 3000);
    },
    handleColorFocus() {
      this.editing = true;
    },
    handleColorBlur() {
      this.editing = false;
    },
    onUIClick() {
      sendRequest(
        '/palette-events',
        'post',
        { Authorization: getSession('jwtoken') },
        {
          'event-type': 'ui-palettes',
          ...this.colorPalettes[this.currentColor],
        },
      )
        .then(() => {})
        .catch(err => console.log(err));
      this.$router.push(`/theme/${this.colorsUrl}`);
    },
    handlePreview(index) {
      sendRequest(
        '/palette-events',
        'post',
        { Authorization: getSession('jwtoken') },
        {
          'event-type': 'ui-palettes',
          ...this.colorPalettes[this.currentColor],
        },
      )
        .then(() => {})
        .catch(err => console.log(err));
      this.$router.push(`/theme/${this.colorsUrl}/${this.outputs[index].output}`);
    },
    maybeNavigate() {
      if (window.innerWidth < 768) {
        sendRequest(
          '/palette-events',
          'post',
          { Authorization: getSession('jwtoken') },
          {
            'event-type': 'ui-palettes',
            ...this.colorPalettes[this.currentColor],
          },
        )
          .then(() => {})
          .catch(err => console.log(err));
        this.$router.push(`/theme/${this.colorsUrl}`);
      }
    },
    keyEvent(e) {
      if (e.target === document.body) {
        switch (e.keyCode) {
          case key.ENTER:
          case key.P:
            sendRequest(
              '/palette-events',
              'post',
              { Authorization: getSession('jwtoken') },
              {
                'event-type': 'ui-palettes',
                ...this.colorPalettes[this.currentColor],
              },
            )
              .then(() => {})
              .catch(err => console.log(err));
            setSession('enterHintShown', true);
            this.$router.push(`/theme/${this.colorsUrl}`);
            break;
          case key.SPACE:
            e.preventDefault();
            this.next();
            if (++this.spaceTime === 10) this.showEnter = true;
            break;
          case key.LEFT:
            this.back();
            break;
          case key.RIGHT:
            this.next();
            break;
          case key.S:
            this.handleSave(undefined, this.currentColor);
            break;
          case key.E:
            this.handleExport(undefined, this.currentColor);
            break;
          case key.N5:
          case key.N4:
          case key.N3:
          case key.N2:
            this.handleNumber(e.keyCode - 48);
            break;
          default:
            break;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .palette-view {
    padding-top: 70px;
    overflow: hidden;

    @include media($notMobile) {
      padding-right: 20px;
    }
  }

  .palette {
    position: relative;
    margin-bottom: 30px;

    @include media($mobile) {
      padding: 0 20px;
      overflow: hidden;
    }
  }

  .colors {
    height: calc(100vh - 180px);
    position: relative;

    .inner {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: $transition;
      border-radius: 5px;
      overflow: hidden;
      display: flex;
      pointer-events: none;

      &.current {
        pointer-events: auto;
      }

      @include media($notMobile) {
        opacity: 0;

        &.current {
          opacity: 1;
        }
      }

      @include media($mobile) {
        flex-direction: column;
        top: 5%;
        height: 90%;

        &.current {
          top: 0;
          height: 100%;
          transform: translate3d(0, 0, 0);
        }

        &.back {
          transform: translate3d(calc(-100% - 15px), 0, 0);
        }

        &.next {
          transform: translate3d(calc(100% + 15px), 0, 0);
        }

        &.backFar {
          transform: translate3d(-200%, 0, 0);
        }

        &.nextFar {
          transform: translate3d(200%, 0, 0);
        }
      }
    }
  }

  .arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    margin-top: -15px;
    cursor: pointer;
    user-select: none;
    padding: 10px 10px 6px;
    background: $bg2;
    border-radius: 100%;
    box-shadow: 0 14px 19px rgba(black, .23);
    opacity: 0;
    transition: $transition;

    @include media($mobile) {
      display: none;
    }

    &:hover {
      color: $blue;
    }

    &.back {
      left: 10px;
      transform: translate3d(-100%, 0, 0);
    }

    &.next {
      right: 10px;
      transform: translate3d(100%, 0, 0);
    }

    i {
      font-size: 30px;
    }
  }

  .actions {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translate(-50%, 0);
    border-radius: 5px;
    padding: 15px 30px;
    background: rgba(white, .2);
    box-shadow: 0 5px 9px rgba(black, .15);
    transition: $transition;

    a {
      margin-right: 20px;
      color: white;
      opacity: .5;

      &:hover, &.active {
        opacity: 1;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    i {
      vertical-align: middle;
    }

    .text {
      font-weight: 500;
      position: relative;
      top: 2px;
      margin-left: 10px;
    }
  }

  .palette {
    &.hover {
      @include media($desktop) {
        .arrow {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .actions {
          background: white;

          a {
            color: $dark;
            opacity: 1;

            &:hover {
              color: $blue;
            }
          }
        }

        .slider {
          opacity: 1;
          pointer-events: auto;
        }
      }
    }
  }

  .container {
    border-radius: 3px;
    background: $bg;
    padding: 30px 30px 30px 50px;

    @include media($tablet) {
      padding: 30px 20px;
    }
  }

  .color-line {
    position: absolute;
    top: 30px;
    left: 20px;
    z-index: 10;
    width: 7px;
    height: calc(100% - 85px);
  }

  .hint {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 233px;
    box-shadow: 0 14px 19px rgba(black, .23);
    transform: translate(-50%, -50%);
    opacity: 0;
    pointer-events: none;
    z-index: 10;

    &.active {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .hint-1, .hint-2 {
    @include media($tablet) {
      display: none !important;
    }
  }

  .hint-3 {
    pointer-events: none !important;

    @include media($desktop) {
      display: none !important;
    }
  }

  .hint-2, .hint-3 {
    transition: $transition;
  }

  .slider {
    position: absolute;
    left: 0;
    bottom: 120px;
    padding: 0 40px;
    z-index: 2;
    opacity: 0;
    pointer-events: none;
    transition: $transition;
  }
</style>
